import { showUserAlert } from "../utils/userErrorMessage";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul", hour12: false })
  : "-";

export default function AppNotices() {
  const [form, setForm] = useState({ title: "", content: "" });
  const [showComposer, setShowComposer] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const company = (localStorage.getItem("email") || "").trim();
  const navigate = useNavigate();

  const loadNotices = useCallback(async () => {
    if (!company) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("app_notices")
      .select("id, title, content, is_active, starts_at, created_at, updated_at")
      .eq("company", company)
      .order("created_at", { ascending: false })
      .range(0, 999);
    if (error) {
      console.error("앱 공지사항 조회 실패:", error);
      showUserAlert("앱 공지사항을 불러오지 못했습니다: " + (error.message || "알 수 없는 오류"));
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, [company]);

  useEffect(() => { loadNotices(); }, [loadNotices]);

  const handleSubmit = async () => {
    const title = form.title.trim();
    const content = form.content.trim();
    if (!company) return showUserAlert("회사 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
    if (!title) return showUserAlert("제목을 입력해 주세요.");
    if (!content) return showUserAlert("내용을 입력해 주세요.");
    if (!window.confirm("해당 글을 공지사항으로 올리겠습니까?")) return;

    setSaving(true);
    try {
      const { error } = await supabase.from("app_notices").insert({
        company,
        title,
        content,
        is_active: true,
        starts_at: new Date().toISOString(),
      });
      if (error) throw error;
      setForm({ title: "", content: "" });
      setShowComposer(false);
      await loadNotices();
      showUserAlert("앱 공지사항이 등록되었습니다.");
    } catch (error) {
      console.error("앱 공지사항 등록 실패:", error);
      showUserAlert("앱 공지사항 등록에 실패했습니다: " + (error.message || "알 수 없는 오류"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <Header />
      <main className="space-y-4 px-4 pb-6 pt-2">
        <h1 className="text-lg font-semibold">앱 공지사항</h1>

        <section className="overflow-x-auto" aria-label="등록된 앱 공지사항">
          <table className="w-full border-collapse border text-sm">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th className="w-16 border px-3 py-2 text-center">순번</th>
                <th className="w-44 border px-3 py-2 text-center">등록일</th>
                <th className="border px-3 py-2 text-left">제목</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="border p-4 text-center">조회 중...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={3} className="border p-4 text-center">등록된 공지사항이 없습니다.</td></tr>
              ) : rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="border px-3 py-2 text-center">
                    <button
                      type="button"
                      className="underline-offset-2 hover:underline"
                      aria-label={`${rows.length - index}번 공지사항 보기`}
                      onClick={() => navigate(`/app-notices/${row.id}`)}
                    >
                      {rows.length - index}
                    </button>
                  </td>
                  <td className="border px-3 py-2 text-center whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                  <td className="border px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="w-full text-left underline-offset-2 hover:underline"
                      onClick={() => navigate(`/app-notices/${row.id}`)}
                    >
                      {row.title}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {showComposer ? (
          <section className="space-y-3" aria-label="앱 공지사항 글쓰기">
            <label className="block text-sm font-medium">
              제목
              <Input
                className="mt-1 w-full"
                value={form.title}
                maxLength={100}
                placeholder="공지사항 제목"
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              />
            </label>
            <label className="block text-sm font-medium">
              내용
              <textarea
                className="mt-1 h-32 w-full resize-y rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.content}
                maxLength={5000}
                placeholder="기사 앱에 표시할 공지사항 내용을 입력해 주세요."
                onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setForm({ title: "", content: "" });
                  setShowComposer(false);
                }}
              >
                취소
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={saving} className="disabled:opacity-50">
                {saving ? "등록 중..." : "확인"}
              </Button>
            </div>
          </section>
        ) : (
          <div className="flex justify-end">
            <Button type="button" onClick={() => setShowComposer(true)}>글 쓰기</Button>
          </div>
        )}
      </main>
    </div>
  );
}
